<template>
  <div class="filter-group-container" :class="computedExpand ? 'expand' : 'collapse'">
    <div class="container-title" @click="handleExpandBtnClick">
      <slot name="title" :title="title" :expand="computedExpand">
        <label class="title-label">{{ title }}</label>
        <div class="expand-btn">
          <i class="btn-icon el-icon-arrow-up"></i>
        </div>
      </slot>
    </div>
    <div ref="content" class="container-content" :style="contentStyle" @transitionend="onTransitionEnd">
      <div class="filter-list">
        <div
          v-for="(filter, filterName) in filterList"
          class="filter-item"
          :key="filterName"
          :class="filter.class"
        >
          <div class="filter-label" :class="(filter.labelContainer?.flex || filter.labelContainer?.showUseAiSwitch) && 'flex-container'">
            <slot :name="filter.labelSlot" :filter="filter" :filterName="filterName">
              <label>{{ filter.label }}</label>
              <div v-if="filter.labelContainer?.showUseAiSwitch" class="use-ai-search-switch">
                <span class="btn-label">{{ $t('admin.application.applicantList.label.useAiSearch') }}</span>
                <el-switch v-model="search[`${filterName}_isUseAiSearch`]" :width="28"></el-switch>
              </div>
            </slot>
          </div>
          <div class="filter-content" :class="filter.enableExclude && 'enable-exclude'">
            <slot :name="filter.contentSlot" :filter="filter" :filterName="filterName">
              <el-select v-if="filter.enableExclude" class="exclude-select" :value="isExcludeFilterValue(filter, filterName)" @change="onExcludeFilterChange($event, filter, filterName)">
                <el-option :label="$t(`common.conditionType.${(filter.multiple || filter.props.multiple) ? 'include' : 'equal'}`)" :value="false"></el-option>
                <el-option :label="$t(`common.conditionType.${(filter.multiple || filter.props.multiple) ? 'exclude' : 'notEqual'}`)" :value="true"></el-option>
              </el-select>
              <el-input
                v-if="filter.elType === 'el-input'"
                v-bind="filter.props"
                v-on="filter.events"
                v-model="search[filterName]"
                :placeholder="filter.placeholder"
              ></el-input>
              <el-select
                v-else-if="filter.elType === 'el-select'"
                v-bind="filter.props"
                v-on="filter.events"
                v-model="search[filterName]"
                :placeholder="filter.placeholder"
              >
                <el-option
                  v-if="showSelectAllOption(filter)"
                  class="filter-select-all-option"
                  :class="{ selected: isSelectAllOptionSelected(filterName, filter) }"
                  :disabled="true"
                  :label="filter.selectAllOption.label"
                  :value="filter.selectAllOption.value"
                  @click.native.stop.prevent="handleSelectAllOptionClick(filterName, filter)"
                ></el-option>
                <el-option
                  v-for="(option, index) in filter.option.data"
                  :key="index"
                  :label="filter.option.labelKey ? option[filter.option.labelKey] : option.label"
                  :value="filter.option.valueKey ? option[filter.option.valueKey] : option"
                  :style="filter.style ? filter.style(option) : ''"
                ></el-option>
              </el-select>
              <custom-remote-el-select
                v-else-if="filter.elType === 'custom-remote-el-select'"
                v-show="!filter.notShow"
                v-bind="filter.props"
                v-on="filter.events"
                v-model="search[filterName]"
                :disabled="filter.disabled"
                :placeholder="filter.placeholder"
                :loading="filter.option.loading"
                :remote-method="filter.option.search"
                :ref="filterName"
                @visible-change="handleRemoteLoadSelectVisibleChange($event, filterName, filter)"
              >
                <el-option
                  v-if="showSelectAllOption(filter)"
                  class="filter-select-all-option"
                  :class="{ selected: isSelectAllOptionSelected(filterName, filter) }"
                  :disabled="true"
                  :label="filter.selectAllOption.label"
                  :value="filter.selectAllOption.value"
                  @click.native.stop.prevent="handleSelectAllOptionClick(filterName, filter)"
                ></el-option>
                <template v-if="filter.option.grouped">
                  <el-option-group
                    v-for="(optionGroup, optionGroupIndex) in filter.option.data"
                    :key="optionGroupIndex"
                    :label="filter.option.groupLabelKey ? optionGroup[filter.option.groupLabelKey] : optionGroup.label"
                  >
                    <el-option
                      v-for="(option, index) in (filter.option.groupOptionsDataKey ? optionGroup[filter.option.groupOptionsDataKey] : optionGroup.children)"
                      :key="index"
                      :label="filter.option.labelKey ? option[filter.option.labelKey] : option.label"
                      :value="filter.option.valueKey ? option[filter.option.valueKey] : option"
                    >
                      <el-tooltip
                        v-if="filter.option.optionTooltip"
                        effect="light"
                        popper-class="light-tooltip-popper"
                        :open-delay="700"
                        :content="filter.option.labelKey ? option[filter.option.labelKey] : option.label"
                      >
                        <div class="label">{{ filter.option.labelKey ? option[filter.option.labelKey] : option.label }}</div>
                      </el-tooltip>
                    </el-option>
                  </el-option-group>
                </template>
                <template v-else>
                  <el-option
                    v-for="(option) in filter.option.data"
                    :key="filter.valueKey ? option[filter.valueKey] : option"
                    :label="filter.option.labelKey ? option[filter.option.labelKey] : option.label"
                    :value="filter.option.valueKey ? option[filter.option.valueKey] : option"
                    :style="filter.style ? filter.style(option) : ''"
                  >
                    <el-tooltip
                      v-if="filter.option.optionTooltip"
                      effect="light"
                      popper-class="light-tooltip-popper"
                      :open-delay="700"
                      :content="filter.option.labelKey ? option[filter.option.labelKey] : option.label"
                    >
                      <div class="label">{{ filter.option.labelKey ? option[filter.option.labelKey] : option.label }}</div>
                    </el-tooltip>
                  </el-option>
                </template>
              </custom-remote-el-select>
              <component
                v-else
                v-bind="filter.props"
                v-on="filter.events"
                v-model="search[filterName]"
                :is="filter.elType"
              ></component>
            </slot>
            <slot :name="filter.extraSlot" :filter="filter" :filterName="filterName"></slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

export default {
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: Object
    },
    filterList: {
      type: Object,
      required: true
    },
    expand: {
      type: Boolean,
      default: undefined
    },
    defaultExpand: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      search: {},
      hasMounted: false,
      isExpand: true,
      contentStyle: {
        height: null
      }
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    currentAccount () {
      return this.getCurrentAccount
    },
    computedExpand: {
      get () {
        if (this.expand === undefined) {
          return this.isExpand
        } else {
          return this.expand
        }
      },
      set (val) {
        this.isExpand = val
        this.$emit('update:expand', val)
      }
    }
  },
  created () {
    this.computedExpand = Boolean(this.defaultExpand)
  },
  mounted () {
    if (this.computedExpand) {
      this.contentStyle.height = null
    } else {
      this.contentStyle.height = '0px'
    }
    this.hasMounted = true
  },
  methods: {
    refreshExpand () {
      if (!this.hasMounted) {
        return
      }
      const expand = this.computedExpand
      if (expand) {
        const el = this.$refs.content
        const height = el.firstElementChild.clientHeight
        this.contentStyle.height = `${height}px`
      } else {
        if (this.contentStyle.height) {
          this.contentStyle.height = '0px'
        } else {
          const height = this.$refs.content.firstElementChild?.clientHeight
          this.contentStyle.height = `${height}px`
          setTimeout(() => {
            this.contentStyle.height = '0px'
          }, 0)
        }
      }
    },
    onTransitionEnd () {
      if (this.contentStyle.height !== '0px') {
        this.contentStyle.height = null
      }
    },
    showSelectAllOption (filter) {
      return Boolean(filter.selectAllOption && filter.multiple && this.getSelectableFilterOptions(filter).length > 0)
    },
    getSelectableFilterOptions (filter) {
      if (_.isFunction(filter.selectAllOption?.getOptions)) {
        return filter.selectAllOption.getOptions(filter) || []
      }
      const data = filter.option?.data || []
      if (filter.option?.grouped) {
        return _.flatMap(data, group => group[filter.option.groupOptionsDataKey || 'children'] || [])
      }
      return data
    },
    getFilterOptionValue (option, filter) {
      const valueKey = filter.option?.valueKey || filter.valueKey
      return valueKey ? _.get(option, valueKey) : option
    },
    isSameFilterOption (left, right, filter) {
      return _.isEqual(this.getFilterOptionValue(left, filter), this.getFilterOptionValue(right, filter))
    },
    hasSelectedFilterOption (option, selectedOptions, filter) {
      return _.some(selectedOptions, selectedOption => this.isSameFilterOption(option, selectedOption, filter))
    },
    isSelectAllOptionSelected (filterName, filter) {
      const selectableOptions = this.getSelectableFilterOptions(filter)
      if (!selectableOptions.length) {
        return false
      }
      const selectedOptions = _.isArray(this.search[filterName]) ? this.search[filterName] : []
      return _.every(selectableOptions, option => this.hasSelectedFilterOption(option, selectedOptions, filter))
    },
    handleSelectAllOptionClick (filterName, filter) {
      const selectableOptions = this.getSelectableFilterOptions(filter)
      if (!selectableOptions.length) {
        return
      }
      const selectedOptions = _.isArray(this.search[filterName]) ? this.search[filterName] : []
      let nextSelectedOptions
      if (this.isSelectAllOptionSelected(filterName, filter)) {
        nextSelectedOptions = _.filter(selectedOptions, selectedOption => {
          return !this.hasSelectedFilterOption(selectedOption, selectableOptions, filter)
        })
      } else {
        nextSelectedOptions = [...selectedOptions]
        _.forEach(selectableOptions, option => {
          if (!this.hasSelectedFilterOption(option, nextSelectedOptions, filter)) {
            nextSelectedOptions.push(option)
          }
        })
      }
      this.$set(this.search, filterName, nextSelectedOptions)
    },
    handleExpandBtnClick () {
      this.computedExpand = !this.computedExpand
      this.$emit('expandChange', this.computedExpand)
    },
    handleRemoteLoadSelectVisibleChange (visible, filterName, filter) {
      const option = filter.option
      if (visible && option.loader) {
        if (option.disableCacheLoadData) {
          option.hasLoad = false
        }
        if (!option.hasLoad && !option.loading) {
          option.loading = true
          Promise.resolve(option.loader()).then((data) => {
            if (filter.loadMore) {
              option.loadedData = data
            } else {
              option.data = data
              option.loadedData = data
            }
            if (filter.initData) {
              option.data = filter.initData()
            }
            option.hasLoad = true
          }).catch((e) => {
            option.hasLoad = false
            // eslint-disable-next-line no-console
            console.error(e)
            return e
          }).finally(() => {
            option.loading = false
          })
        }
      }
      if (visible && filter.loadMore) {
        this.$nextTick(() => {
          if (this.$refs[filterName]) {
            const elSelect = this.$refs[filterName][0]
            const el = elSelect?.popperElm.querySelector('.el-select-dropdown__wrap')
            if (el && !el.hasAttribute('filter-scroll-listened')) {
              el.setAttribute('filter-scroll-listened', true)
              el.addEventListener('scroll', () => this.handlerScroll(el, filter))
            }
          }
        })
      }
      if (!visible && option.isSearched) {
        this.$nextTick(() => {
          option.data = option.loadedData
          option.isSearched = false
        })
      }
      if (!filter.events) {
        return
      }
      const handler = filter.events['visible-change']
      if (handler) {
        handler(visible, filterName, filter)
      }
    },
    handlerScroll (el, filter) {
      const condition = el.scrollHeight - el.scrollTop - 20 <= el.clientHeight
      if (condition) {
        filter.loadMore(filter)
      }
    },
    isExcludeFilterValue (filter, filterName) {
      if (filter.defaultExclude && !this.search._exclude) {
        this.$set(this.search, '_exclude', [filterName])
      }
      return _.includes(this.search._exclude, filterName)
    },
    onExcludeFilterChange (value, filter, filterName) {
      if (!this.search._exclude) {
        this.$set(this.search, '_exclude', [])
      }
      const excludeSearch = this.search._exclude
      if (value) {
        excludeSearch.push(filterName)
      } else {
        excludeSearch.splice(excludeSearch.indexOf(filterName), 1)
      }
    }
  },
  watch: {
    computedExpand () {
      this.refreshExpand()
    },
    value: {
      immediate: true,
      handler () {
        if (!this.value) {
          this.$emit('change', {})
        } else {
          this.search = this.value
        }
      }
    },
    '$i18n.locale' () {
      _.forEach(this.filterList, (filter, filterName) => {
        if (filter.loadMore && this.$refs[filterName]) {
          const elSelect = this.$refs[filterName][0]
          const el = elSelect?.popperElm.querySelector('.el-select-dropdown__wrap')
          if (el && el.hasAttribute('filter-scroll-listened')) {
            el.removeAttribute('filter-scroll-listened')
            el.removeEventListener('scroll', () => this.handlerScroll)
          }
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-group-container {
  .container-title {
    display: flex;
    align-items: center;
    padding: 16px 0;

    .title-label {
      margin-left: 0;
      margin-right: auto;
      color: #606266;
      font-size: 16px;
      font-weight: bold;
    }

    .expand-btn {
      margin-left: auto;
      margin-right: 0;

      .btn-icon {
        transform: rotateZ(0);
        transition: transform .3s ease-in-out;
      }
    }
  }

  .container-content {
    transition: height .3s ease-in-out;
    overflow: hidden;

    .filter-list {
      .filter-item {
        margin-bottom: 14px;
        padding-bottom: 14px;
        border-bottom: 1px solid #e8f1f4;

        &:last-child {
          margin-bottom: 0;
          border-bottom: 0;
        }

        .filter-label {
          color: #606266;
          font-size: 14px;

          &.flex-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .use-ai-search-switch {
            .btn-label {
              font-size: 10px;
              margin-right: 2px;
            }

            ::v-deep {
              .el-switch {
                height: 14px;
                .el-switch__core {
                  height: 14px;
                  &::after {
                    width: 10px;
                    height: 10px;
                  }
                }

              }
              .is-checked {
                .el-switch__core {
                  &::after {
                    margin-left: -11px;
                  }
                }
              }
            }
          }
        }

        .filter-content {
          margin-top: 10px;

          &.enable-exclude {
            display: flex;

            .exclude-select {
              width: 46%;
              ::v-deep {
                .el-input {
                  height: 100%;
                  .el-input__inner {
                    height: 100%;
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;
                    border-right: 0;
                  }
                }
              }
            }
            > :not(.exclude-select) ::v-deep {
              .el-input__inner {
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
              }
            }
          }

          > ::v-deep {
            .el-cascader {
              width: 100%;
              line-height: 32px;
            }
          }
        }
      }
    }
  }

  &.expand {

  }

  &.collapse {
    .container-title {
      .expand-btn {
        .btn-icon {
          transform: rotateZ(-180deg);
        }
      }
    }
  }
}
.page-en {
  .filter-group-container{
    .container-content {
      .filter-list {
        .filter-item {
          .filter-content.enable-exclude {
            .exclude-select {
              width: 70%;
            }
          }
        }
      }
    }
  }
}
</style>
<style lang="scss">
.el-select-dropdown__item.filter-select-all-option.is-disabled {
  color: #606266;
  cursor: pointer;

  &:hover {
    background-color: #f5f7fa;
  }
}
</style>
